import {
    AccountingStatus,
    Assignment,
    DisciplinaryMatchEntryStatus,
    GoalPeriod,
    GoalType,
    League,
    LeagueGroup,
    Match,
    MatchGoal,
    MatchGoalSummary,
    MatchStatus,
    Official,
    SanctionType,
    Stadium,
    Team,
    TeamLeagueMembership,
    SENIOR_CATEGORY_ID,
} from '../types';

const extractLeagueMemberships = (teamRow: any): TeamLeagueMembership[] => {
    const membershipRows = Array.isArray(teamRow.league_group_teams)
        ? teamRow.league_group_teams
        : Array.isArray(teamRow.leagueGroupTeams)
            ? teamRow.leagueGroupTeams
            : [];

    return membershipRows
        .map((row: any): TeamLeagueMembership | null => {
            const leagueGroup = row.league_group || row.leagueGroup;
            const leagueId =
                row.league_id ||
                row.leagueId ||
                leagueGroup?.league_id ||
                leagueGroup?.leagueId ||
                null;

            if (!leagueId) return null;

            return {
                leagueId,
                leagueGroupId: leagueGroup?.id ?? row.league_group_id ?? row.leagueGroupId ?? null,
                season: leagueGroup?.season ?? row.season ?? null,
            };
        })
        .filter((entry): entry is TeamLeagueMembership => !!entry);
};

export const mapTeam = (t: any): Team => {
    const leagueMemberships = extractLeagueMemberships(t);

    return {
        id: t.id,
        code: t.code,
        name: t.name,
        nameAr: t.name_ar ?? '',
        fullName: t.full_name ?? null,
        abriv: t.abriv ?? null,
        nameFaf: t.name_faf ?? null,
        logoUrl: t.logo_url ?? null,
        primaryColor: t.primary_color ?? null,
        secondaryColor: t.secondary_color ?? null,
        foundedYear: t.founded_year ?? null,
        isArchived: !!t.is_archived,
        isExternal:
            typeof t.is_external === 'boolean'
                ? t.is_external
                : (t as any)?.isExternal ?? undefined,
        externalOrigin: t.external_origin ?? (t as any)?.externalOrigin ?? null,
        wilaya: t.wilaya ?? null,
        engagement: t.engagement ?? null,
        fafId: t.faf_id ?? null,
        categoryId: t.category_id ?? SENIOR_CATEGORY_ID,
        clubTeamId: t.club_team_id ?? null,
        createdAt: t.created_at,
        createdBy: t.created_by ?? null,
        createdByName: t.createdByName ?? 'Système',
        updatedAt: t.updated_at ?? null,
        updatedBy: t.updated_by ?? null,
        updatedByName: t.updatedByName ?? null,
        leagueMemberships: leagueMemberships.length > 0 ? leagueMemberships : undefined,
    };
};

export const mapStadium = (s: any): Stadium => ({
    id: s.id,
    name: s.name,
    nameAr: s.name_ar ?? null,
    locationId: s.location_id ?? null,
    latitude: s.latitude ?? null,
    longitude: s.longitude ?? null,
    isArchived: !!s.is_archived,
    createdAt: s.created_at,
    createdBy: s.created_by ?? null,
    createdByName: s.createdByName ?? 'Système',
    updatedAt: s.updated_at ?? null,
    updatedBy: s.updated_by ?? null,
    updatedByName: s.updatedByName ?? null,
});

export const mapOfficial = (o: any): Official => {
    const westernName = `${o.first_name || ''} ${o.last_name || ''}`.trim();
    const arabicName = `${o.first_name_ar || ''} ${o.last_name_ar || ''}`.trim();
    const fullName = [westernName, arabicName && `(${arabicName})`].filter(Boolean).join(' ');

    const rawUnavailabilities = Array.isArray(o.unavailabilities)
        ? o.unavailabilities
        : Array.isArray(o.official_unavailabilities)
            ? o.official_unavailabilities
            : [];

    const unavailabilities = rawUnavailabilities.map((u: any) => ({
        id: u.id,
        startDate: u.start_date,
        endDate: u.end_date,
        reason: u.reason,
        isApproved: !!u.is_approved,
    }));

    const rawEmails: string[] = Array.isArray(o.emails)
        ? o.emails
        : Array.isArray(o.official_emails)
            ? o.official_emails.map((e: any) => (e.email || e.address || '').toLowerCase()).filter(Boolean)
            : [];

    const uniqueEmails = Array.from(new Set(rawEmails.map((email) => email.trim().toLowerCase())));
    if (o.email) {
        const normalized = o.email.toLowerCase();
        if (!uniqueEmails.includes(normalized)) uniqueEmails.unshift(normalized);
    }

    return {
        id: o.id,
        firstName: o.first_name,
        lastName: o.last_name,
        firstNameAr: o.first_name_ar,
        lastNameAr: o.last_name_ar,
        fullName,
        category: o.category,
        locationId: o.location_id ?? null,
        address: o.address ?? null,
        position: o.position ?? null,
        email: o.email ?? null,
        emails: uniqueEmails.length > 0 ? uniqueEmails : undefined,
        phone: o.phone ?? null,
        bankAccountNumber: o.bank_account_number ?? null,
        dateOfBirth: o.date_of_birth ?? null,
        isActive: !!o.is_active,
        isArchived: !!o.is_archived,
        userId: o.user_id ?? null,
        unavailabilities,
        createdAt: o.created_at,
        createdBy: o.created_by ?? null,
        createdByName: o.createdByName ?? 'Système',
        updatedAt: o.updated_at ?? null,
        updatedBy: o.updated_by ?? null,
        updatedByName: o.updatedByName ?? null,
    };
};

export const mapAssignment = (a: any): Assignment => ({
    id: a.id,
    matchId: a.match_id,
    role: a.role,
    officialId: a.official_id ?? null,
    originalOfficialId: a.original_official_id ?? null,
    isConfirmed: !!a.is_confirmed,
    confirmedAt: a.confirmed_at ?? null,
    isRejectedByOfficial: !!a.is_rejected_by_official,
    rejectionReason: a.rejection_reason ?? null,
    rejectionComment: a.rejection_comment ?? null,
    travelDistanceInKm: a.travel_distance_km ?? null,
    indemnityAmount: a.indemnity_amount ?? null,
    notes: a.notes ?? null,
    isAbsent: !!a.is_absent,
    isNotAssigned: !!a.is_not_assigned,
    isPaid: !!a.is_paid,
    paidAt: a.paid_at ?? null,
    paymentBatchId: a.payment_batch_id ?? null,
    paymentBatchReference:
        a.payment_batch_reference ??
        a.paymentBatchReference ??
        a.payment_batch?.reference ??
        null,
    paymentAmount: a.payment_amount ?? null,
    payment_amount: a.payment_amount ?? null,
    createdBy: a.created_by,
    lastMissionOrderSentAt: a.last_mission_order_sent_at ?? null,
    officialName: a.official?.full_name ?? null,
    officialLocationId: a.official?.location_id ?? null,
    official: a.official?.id ? mapOfficial(a.official) : null,
});

export const mapLeague = (l: any): League => ({
    id: l.id,
    name: l.name,
    name_ar: l.name_ar,
    level: l.level,
    parent_league_id: l.parent_league_id ?? null,
});

export const mapLeagueGroup = (lg: any): LeagueGroup => ({
    id: lg.id,
    name: lg.name,
    name_ar: lg.name_ar,
    league_id: lg.league_id,
    season: lg.season,
    teamIds: Array.isArray(lg.league_group_teams)
        ? lg.league_group_teams.map((t: any) => t.team_id)
        : lg.teamIds || [],
    kind: (lg.kind === 'cup' ? 'cup' : 'regular'),
    organizedCompetitionName: lg.organized_competition_name ?? lg.organizedCompetitionName ?? null,
    categoryId: lg.category_id ?? lg.categoryId ?? SENIOR_CATEGORY_ID,
    fafGroupId: lg.faf_group_id ?? null,
    fafCompetitionId: lg.faf_competition_id ?? null,
});

const mapDbStatusToUi = (status: string): MatchStatus => {
    switch (status) {
        case 'completed':
            return MatchStatus.COMPLETED;
        case 'postponed':
            return MatchStatus.POSTPONED;
        case 'cancelled':
            return MatchStatus.CANCELLED;
        case 'in_progress':
            return MatchStatus.IN_PROGRESS;
        case 'scheduled':
        default:
            return MatchStatus.SCHEDULED;
    }
};

const normalizeGoalType = (goalType?: string): GoalType => {
    switch (goalType) {
        case 'penalty':
            return GoalType.PENALTY;
        case 'free_kick':
            return GoalType.FREE_KICK;
        case 'header':
            return GoalType.HEADER;
        case 'own_goal':
            return GoalType.OWN_GOAL;
        case 'penalty_shootout':
            return GoalType.PENALTY_SHOOTOUT;
        case 'other':
            return GoalType.OTHER;
        case 'open_play':
        default:
            return GoalType.OPEN_PLAY;
    }
};

const normalizeGoalPeriod = (period?: string): GoalPeriod => {
    switch (period) {
        case 'second_half':
            return GoalPeriod.SECOND_HALF;
        case 'extra_time':
            return GoalPeriod.EXTRA_TIME;
        case 'penalty_shootout':
            return GoalPeriod.PENALTY_SHOOTOUT;
        case 'first_half':
        default:
            return GoalPeriod.FIRST_HALF;
    }
};

const nameToTrimmed = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const nameFromParts = (first?: unknown, last?: unknown): string | null => {
    const firstName = typeof first === 'string' ? first.trim() : '';
    const lastName = typeof last === 'string' ? last.trim() : '';
    const combined = `${firstName} ${lastName}`.trim();
    return combined.length > 0 ? combined : null;
};

// French-preferred display name. Used for goal editing/saving and as the default display.
const resolveFrenchName = (entity?: any): string | null => {
    if (!entity) return null;
    return nameToTrimmed(entity.full_name) ?? nameFromParts(entity.first_name, entity.last_name);
};

// Arabic-only display name. Returns null when the player has no Arabic name on file,
// so the UI can fall back to the French/override name.
const resolveArabicName = (entity?: any): string | null => {
    if (!entity) return null;
    return nameToTrimmed(entity.full_name_ar) ?? nameFromParts(entity.first_name_ar, entity.last_name_ar);
};

export const mapMatchGoal = (g: any): MatchGoal => {
    const player = g.scorer || g.player;
    const assist = g.assist || g.assistPlayer;
    const scoringTeam = g.scoringTeam || g.team;

    const playerName = resolveFrenchName(player);
    const assistName = resolveFrenchName(assist);
    const playerNameAr = resolveArabicName(player);
    const assistNameAr = resolveArabicName(assist);
    const overrideScorer: string | null = g.scorer_name_override ?? null;
    const overrideAssist: string | null = g.assist_name_override ?? null;
    const isOwnGoal = !!g.is_own_goal;

    const displayScorerName =
        overrideScorer ?? playerName ?? (isOwnGoal ? 'Contre son camp' : 'But inscrit');
    const displayAssistName = overrideAssist ?? assistName ?? undefined;
    const displayScorerNameAr = overrideScorer ?? playerNameAr ?? null;
    const displayAssistNameAr = overrideAssist ?? assistNameAr ?? null;

    return {
        id: g.id,
        matchId: g.match_id,
        teamId: g.scoring_team_id,
        teamName: scoringTeam?.name ?? null,
        playerId: g.player_id ?? player?.id ?? null,
        playerName,
        displayScorerName,
        displayScorerNameAr,
        scorerNameOverride: overrideScorer,
        assistPlayerId: g.assist_player_id ?? assist?.id ?? null,
        assistName,
        displayAssistName,
        displayAssistNameAr,
        assistNameOverride: overrideAssist,
        goalType: normalizeGoalType(g.goal_type),
        period: normalizeGoalPeriod(g.period),
        minute: typeof g.minute === 'number' ? g.minute : g.minute ?? null,
        stoppageTime:
            typeof g.stoppage_time === 'number' ? g.stoppage_time : g.stoppage_time ?? null,
        countsTowardScore: g.counts_toward_score ?? true,
        isOwnGoal,
        description: g.description ?? null,
        createdAt: g.created_at,
        createdBy: g.created_by ?? null,
        updatedAt: g.updated_at ?? null,
        updatedBy: g.updated_by ?? null,
        fafEventId: g.faf_event_id ?? null,
    };
};

const computeGoalSummary = (
    goals: MatchGoal[],
    homeTeamId: string,
    awayTeamId: string
): MatchGoalSummary | undefined => {
    if (!goals.length) return undefined;

    return goals.reduce(
        (acc, goal) => {
            const bucket = goal.countsTowardScore ? acc.regulation : acc.special;
            if (goal.teamId === homeTeamId) bucket.home += 1;
            else if (goal.teamId === awayTeamId) bucket.away += 1;
            return acc;
        },
        {
            regulation: { home: 0, away: 0 },
            special: { home: 0, away: 0 },
        } as MatchGoalSummary
    );
};

const computeSanctionsSummary = (
    rawEntries: any[] | undefined,
    homeTeamId: string,
    awayTeamId: string
): Match['sanctionsSummary'] => {
    if (!Array.isArray(rawEntries) || rawEntries.length === 0) return undefined;

    const active = rawEntries.filter((s) => !s.is_cancelled);

    const makeSide = () => ({ yellow: 0, red: 0, player: { yellow: 0, red: 0 }, staff: { yellow: 0, red: 0 }, team: { count: 0 }, fines: 0 });
    const summary = {
        home: makeSide(),
        away: makeSide(),
        total: 0,
        totalFines: 0,
    };

    for (const s of active) {
        const bucket =
            s.team_id === homeTeamId ? summary.home :
            s.team_id === awayTeamId ? summary.away :
            null;

        if (!bucket) continue;

        const fineAmount = typeof s.fine_amount === 'number' ? s.fine_amount : 0;
        if (fineAmount > 0) {
            bucket.fines += fineAmount;
            summary.totalFines += fineAmount;
        }

        // Team-level sanctions (fines, warnings, reports, etc.) — not card-based
        if (s.subject_type === 'team') {
            bucket.team.count += 1;
            summary.total += 1;
            continue;
        }

        const isYellow = s.type === SanctionType.YELLOW_CARD;
        const isRed =
            s.type === SanctionType.RED_CARD_DIRECT ||
            s.type === SanctionType.RED_CARD_TWO_YELLOWS;

        if (!isYellow && !isRed) continue;

        if (isYellow) bucket.yellow += 1;
        else bucket.red += 1;
        summary.total += 1;

        const isPlayer = s.subject_type === 'player';
        const sub = isPlayer ? bucket.player : bucket.staff;
        if (isYellow) sub.yellow += 1;
        else sub.red += 1;
    }

    return summary;
};

export const mapMatch = (m: any): Match | null => {
    if (!m.homeTeam || !m.awayTeam) return null;

    const league = m.leagueGroup?.league
        ? mapLeague(m.leagueGroup.league)
        : ({ id: '', name: 'Ligue non définie', name_ar: '', level: 0, parent_league_id: null } as League);

    const leagueGroup: Match['leagueGroup'] = m.leagueGroup
        ? {
            id: m.leagueGroup.id,
            name: m.leagueGroup.name,
            name_ar: m.leagueGroup.name_ar,
            kind: m.leagueGroup.kind === 'cup' ? 'cup' : 'regular',
            organizedCompetitionName:
                m.leagueGroup.organized_competition_name
                ?? m.leagueGroup.organizedCompetitionName
                ?? null,
            league,
        }
        : { id: '', name: 'Groupe non défini', name_ar: '', kind: 'regular', organizedCompetitionName: null, league };

    const homeTeam = mapTeam(m.homeTeam);
    const awayTeam = mapTeam(m.awayTeam);

    const rawGoals = Array.isArray(m.goals)
        ? m.goals
        : Array.isArray(m.match_goals)
            ? m.match_goals
            : Array.isArray(m.matchGoals)
                ? m.matchGoals
                : [];

    const goals = rawGoals
        .map(mapMatchGoal)
        .sort((a, b) => {
            const periodOrder: Record<GoalPeriod, number> = {
                [GoalPeriod.FIRST_HALF]: 0,
                [GoalPeriod.SECOND_HALF]: 1,
                [GoalPeriod.EXTRA_TIME]: 2,
                [GoalPeriod.PENALTY_SHOOTOUT]: 3,
            };
            const periodDiff = periodOrder[a.period] - periodOrder[b.period];
            if (periodDiff !== 0) return periodDiff;

            const minuteA = a.minute ?? Number.MAX_SAFE_INTEGER;
            const minuteB = b.minute ?? Number.MAX_SAFE_INTEGER;
            if (minuteA !== minuteB) return minuteA - minuteB;

            const stoppageA = a.stoppageTime ?? Number.MAX_SAFE_INTEGER;
            const stoppageB = b.stoppageTime ?? Number.MAX_SAFE_INTEGER;
            if (stoppageA !== stoppageB) return stoppageA - stoppageB;

            // Handle minimal goal objects without createdAt
            if (a.createdAt && b.createdAt) {
                return a.createdAt.localeCompare(b.createdAt);
            }
            return 0;
        });

    const goalSummary = computeGoalSummary(goals, homeTeam.id, awayTeam.id);
    const sanctionsSummary = computeSanctionsSummary(m.sanctionEntries, homeTeam.id, awayTeam.id);

    return {
        id: m.id,
        season: m.season,
        gameDay: m.game_day,
        leagueGroup,
        matchDate: m.match_date ?? null,
        matchTime: m.match_time ?? null,
        homeTeam,
        awayTeam,
        homeScore: m.home_score ?? null,
        awayScore: m.away_score ?? null,
        stadium: m.stadium ? mapStadium(m.stadium) : null,
        status: mapDbStatusToUi(m.status),
        assignments: Array.isArray(m.assignments) ? m.assignments.map(mapAssignment) : [],
        isSheetSent: !!m.is_sheet_sent,
        hasUnsentChanges: !!m.has_unsent_changes,
        isArchived: !!m.is_archived,
        isExternalCompetition: !!m.is_external_competition,
        externalCompetitionName: m.external_competition_name ?? null,
        originatingLeagueId: m.originating_league_id ?? null,
        cupRound: m.cup_round ?? null,
        isNoFans: !!m.is_no_fans,
        createdAt: m.created_at,
        createdBy: m.created_by,
        createdByName: m.createdByName ?? 'Système',
        updatedAt: m.updated_at ?? null,
        updatedBy: m.updated_by ?? null,
        updatedByName: m.updatedByName ?? null,
        accountingStatus: (m.accounting_status as AccountingStatus) ?? AccountingStatus.NOT_ENTERED,
        rejectionReason: m.rejection_reason ?? null,
        rejectionComment: m.rejection_comment ?? null,
        validatedBy: m.validated_by ?? null,
        validatedByName: m.validatedBy?.full_name,
        validatedAt: m.validated_at ?? null,
        accountingPeriodId: m.accounting_period_id ?? null,
        goals,
        goalSummary,
        sanctionsSummary,
        disciplinaryEntryStatus:
            (m.disciplinary_entry_status as DisciplinaryMatchEntryStatus) ?? DisciplinaryMatchEntryStatus.WAITING,
        disciplinaryEntryStatusUpdatedAt: m.disciplinary_entry_status_updated_at ?? null,
        disciplinaryEntryStatusUpdatedBy: m.disciplinary_entry_status_updated_by ?? null,
        forfait_type: m.forfait_type ?? null,
        forfaitReason: m.forfait_reason ?? null,
        reports: {
            hasEvents: (m.eventsCount?.[0]?.count ?? 0) > 0,
            hasIncidents: (m.incidentsCount?.[0]?.count ?? 0) > 0,
            hasNotes: (m.notesCount?.[0]?.count ?? 0) > 0,
            eventsCount: m.eventsCount?.[0]?.count ?? 0,
            incidentsCount: m.incidentsCount?.[0]?.count ?? 0,
            notesCount: m.notesCount?.[0]?.count ?? 0,
        },
        fafFinalArUrl: m.fafMatches?.[0]?.pdf_final_ar_url ?? null,
        hasFafData: (m.fafMatches?.[0]?.fafEventCount?.[0]?.count ?? 0) > 0,
    };
};
